import React, { useEffect } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import Input from "../../widgets/Input";
import Tooltip from "../../widgets/ToolTip";
import { Settings } from "lucide-react";
import { CustomSelect } from "../../workflow/common/node-ui";

interface ApiEndpointType {
  api_name: string;
  endpoint: string;
  method: string;
  api_key?: string;
  headers?: Record<string, string> | string;
  rate_limit?: number;
  is_active: boolean;
  target_path?: string; // ✅ Added field
  request_body?: Record<string, any> | string;
  // Response handling
  ingest_mode?: string;        // FILES | DATA
  data_granularity?: string;   // WHOLE | PER_ITEM
  data_format?: string;        // JSON | TXT
  filename_field?: string;
  data_file_field?: string;
  // Pagination
  pagination_type?: string;    // NONE | PAGE | OFFSET | CURSOR
  page_param?: string;
  size_param?: string;
  page_size?: number;
  start_page?: number;
  offset_param?: string;
  cursor_param?: string;
  next_cursor_path?: string;
  max_pages?: number;
}

const defaultValues: ApiEndpointType = {
  api_name: "",
  endpoint: "",
  method: "",
  api_key: "",
  headers: { "Content-Type": "application/json" },
  rate_limit: 1000,
  is_active: true,
  target_path: "", // ✅ Added field
  request_body: "",
  ingest_mode: "FILES",
  data_granularity: "PER_ITEM",
  data_format: "JSON",
  filename_field: "",
  data_file_field: "",
  pagination_type: "NONE",
  page_param: "page",
  size_param: "limit",
  page_size: 100,
  start_page: 1,
  offset_param: "offset",
  cursor_param: "cursor",
  next_cursor_path: "",
  max_pages: 50,
};

const ApiEndpointForm: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  config: ApiEndpointType | null;
  onSubmit: (data: ApiEndpointType) => void;
  isLoading: boolean;
}> = ({ isOpen, onClose, config, onSubmit, isLoading }) => {
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<ApiEndpointType>({
    defaultValues,
    mode: "onChange", // Show errors when field loses focus
  });

  const ingestMode = watch("ingest_mode");
  const dataGranularity = watch("data_granularity");
  const paginationType = watch("pagination_type");
  const method = watch("method");

  useEffect(() => {
    if (isOpen) {
      if (config) {
        reset({ ...config, headers: config.headers || { "Content-Type": "application/json" } });
      } else {
        reset(defaultValues);
      }
    }
  }, [isOpen, config, reset]);

  const submitHandler: SubmitHandler<ApiEndpointType> = (data) => {
    let parsedHeaders = { "Content-Type": "application/json" };
    
    // Parse headers if it's a string
    if (typeof data.headers === 'string') {
      try {
        parsedHeaders = JSON.parse(data.headers);
      } catch (error) {
        parsedHeaders = { "Content-Type": "application/json" };
      }
    }

    // Parse request body JSON (only meaningful for non-GET)
    let parsedBody: any = undefined;
    if (typeof data.request_body === "string") {
      const trimmed = data.request_body.trim();
      if (trimmed) {
        try {
          parsedBody = JSON.parse(trimmed);
        } catch {
          parsedBody = trimmed; // send as-is if not valid JSON
        }
      }
    } else if (data.request_body) {
      parsedBody = data.request_body;
    }

    onSubmit({
      ...data,
      headers: parsedHeaders,
      request_body: parsedBody,
      rate_limit: Number(data.rate_limit) || 1000,
    });
  };

  if (!isOpen) return null;

  return (
<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
  <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[85vh] flex flex-col">
    
    {/* ✅ Sticky Header */}
    <div className="flex justify-between items-center border-b border-gray-200 p-6 sticky top-0 bg-white z-10">
      <h3 className="text-xl font-semibold text-gray-900">
        {config ? "Edit API Configuration" : "Create API Configuration"}
      </h3>
      <Tooltip content="Close">
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
          disabled={isLoading}
          type="button"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </Tooltip>
    </div>

    {/* ✅ Scrollable Main Form Content */}
    <div className="overflow-y-auto px-6 py-4 space-y-6 flex-1">
      <div className="flex items-center gap-2">
        <Settings className="w-5 h-5 text-gray-600" />
        <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
      </div>

      <form
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
        autoComplete="off"
      >
        {/* --- All form inputs stay same --- */}

          {/* API Name */}
           <Input<ApiEndpointType>
              name="api_name"
              control={control}
              label="API Name"
              placeholder="Enter API Name (e.g., User Management API)"
              rules={{ 
                required: "API Name is required",
                minLength: {
                  value: 3,
                  message: "API Name must be at least 3 characters"
                },
                pattern: {
                  value: /^[A-Za-z0-9\s]*$/,
                  message: "Only alphabets, numbers, and spaces are allowed"
                }
              }}
              disabled={isLoading}
            />
          {/* Endpoint URL */}
          <div  className="col-span-1 md:col-span-2 px-0">

           <Input<ApiEndpointType>
              name="endpoint"
              control={control}
              label="Endpoint URL"
              placeholder="https://api.example.com/v1/endpoint"
              rules={{ 
                required: "Endpoint URL is required",
                pattern: {
                  value: /^https?:\/\/[^<>]+$/,
                  message: "Please enter a valid URL starting with http:// or https:// and without < or > characters"
                }
              }}
              type="text"
              disabled={isLoading}
            />
          </div>

          

          {/* Method */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              HTTP Method <span className="text-red-500">*</span>
            </label>
            <Controller
              name="method"
              control={control}
              rules={{ required: "Method is required" }}
              render={({ field }) => (
                <CustomSelect
                  value={field.value || ""}
                  onChange={field.onChange}
                  disabled={isLoading}
                  placeholder="Select"
                  options={[
                    { value: "GET", label: "GET" },
                    { value: "POST", label: "POST" },
                    { value: "PUT", label: "PUT" },
                    { value: "DELETE", label: "DELETE" },
                  ]}
                />
              )}
            />
            {errors.method && <p className="text-xs text-red-500">{errors.method.message}</p>}
          </div>

          {/* API Key */}
          <div className="space-y-2">
            <Input<ApiEndpointType>
              name="api_key"
              control={control}
              label="API Key"
              placeholder="Enter API Key (optional)"
              rules={{
                minLength: {
                  value: 8,
                  message: "API Key must be at least 8 characters if provided"
                },
                pattern: {
                  value: /^[^<>]*$/,
                  message: "API Key cannot contain < or > characters"
                }
              }}
              type="password"
              disabled={isLoading}
              autoComplete="new-password"
            />
          </div>

          {/* Headers - Full Width */}
          <div className="col-span-1 md:col-span-2 space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Headers (JSON) <span className="text-red-500">*</span>
            </label>
            <div>
              
            </div>
            <Controller
              name="headers"
              control={control}
              rules={{
                required: "Headers are required",
                validate: (value) => {
                  if (typeof value === 'string') {
                    try {
                      const parsed = JSON.parse(value);
                      if (typeof parsed !== 'object' || parsed === null) {
                        return "Headers must be a valid JSON object";
                      }
                      return true;
                    } catch {
                      return "Invalid JSON format";
                    }
                  }
                  return true;
                }
              }}
              render={({ field }) => (
<div>                <textarea
                  {...field}
                  rows={4}
                  disabled={isLoading}
                  value={typeof field.value === "string" ? field.value : JSON.stringify(field.value, null, 2)}
                  onChange={(e) => field.onChange(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 border-gray-300"
                />
                <p className="text-xs text-gray-500">Enter headers in JSON format</p>
                </div>
              )}

            />
          </div>

          {/* Request Body — only for write methods */}
          {method && method !== "GET" && (
            <div className="col-span-1 md:col-span-2 space-y-1">
              <label className="text-sm font-semibold text-gray-700">
                Request Body (JSON){" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <Controller
                name="request_body"
                control={control}
                render={({ field }) => (
                  <textarea
                    rows={4}
                    disabled={isLoading}
                    placeholder='{ "status": "open", "type": "invoice" }'
                    value={
                      typeof field.value === "string"
                        ? field.value
                        : field.value
                          ? JSON.stringify(field.value, null, 2)
                          : ""
                    }
                    onChange={(e) => field.onChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 border-gray-300"
                  />
                )}
              />
              <p className="text-xs text-gray-500">
                Sent as the {method} body. When pagination is enabled, the page / offset /
                cursor params are merged into this body automatically.
              </p>
            </div>
          )}

          {/* Rate Limit */}

            <Input<ApiEndpointType>
              name="rate_limit"
              control={control}
              label="Rate Limit (requests per minute)"
              placeholder="1000"
              rules={{
                required: "Rate limit is required",
                min: {
                  value: 1,
                  message: "Rate limit must be at least 1"
                },
                max: {
                  value: 10000,
                  message: "Rate limit cannot exceed 10,000"
                }
              }}
              type="number"
              disabled={isLoading}
            />

          {/* Active Toggle */}
          <div className="flex flex-col items-start justify-start">
            <label htmlFor="is_active" className="text-sm font-medium text-gray-700 mb-3">
              API Configuration Active
            </label>
            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <button
                  type="button"
                  role="switch"
                  aria-checked={field.value}
                  disabled={isLoading}
                  onClick={() => !isLoading && field.onChange(!field.value)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none 
          ${field.value ? "bg-blue-600" : "bg-gray-300"} 
          ${isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform
            ${field.value ? "translate-x-6" : "translate-x-1"}`}
                  />
                </button>
              )}
            />
          </div>

          {/* ───── Response handling ───── */}
          <div className="col-span-1 md:col-span-2 border-t border-gray-200 pt-5 mt-1 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Response handling</h4>
              <p className="text-xs text-gray-500 mt-0.5">
                Tell DocxIQ what the API returns so it knows how to turn the response into documents.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Ingest mode</label>
                <Controller
                  name="ingest_mode"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      value={field.value || "FILES"}
                      onChange={field.onChange}
                      disabled={isLoading}
                      options={[
                        { value: "FILES", label: "Files", sublabel: "Response contains files" },
                        { value: "DATA", label: "Data", sublabel: "Convert response to documents" },
                      ]}
                    />
                  )}
                />
                <p className="text-xs text-gray-500">
                  {ingestMode === "DATA"
                    ? "The JSON response is saved as document(s) and processed like uploads."
                    : "Each item under the path below is downloaded as a file."}
                </p>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  {ingestMode === "DATA" ? "Data path" : "File-list path"}
                </label>
                <Input<ApiEndpointType>
                  name="target_path"
                  control={control}
                  placeholder="e.g. data.items (leave empty for whole response)"
                  rules={{
                    pattern: {
                      value: /^[A-Za-z0-9._/-]*$/,
                      message: "Use a dot-path like data.items",
                    },
                  }}
                  disabled={isLoading}
                />
                <p className="text-xs text-gray-500">
                  {ingestMode === "DATA"
                    ? "Dot-path to the array/object to ingest. Empty = whole response."
                    : "Dot-path to the array of files in the response."}
                </p>
              </div>
            </div>

            {ingestMode === "DATA" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Granularity</label>
                  <Controller
                    name="data_granularity"
                    control={control}
                    render={({ field }) => (
                      <CustomSelect
                        value={field.value || "PER_ITEM"}
                        onChange={field.onChange}
                        disabled={isLoading}
                        options={[
                          { value: "PER_ITEM", label: "Per record", sublabel: "One doc per item" },
                          { value: "WHOLE", label: "Whole response", sublabel: "One doc" },
                        ]}
                      />
                    )}
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Format</label>
                  <Controller
                    name="data_format"
                    control={control}
                    render={({ field }) => (
                      <CustomSelect
                        value={field.value || "JSON"}
                        onChange={field.onChange}
                        disabled={isLoading}
                        options={[
                          { value: "JSON", label: "JSON (.json)" },
                          { value: "TXT", label: "Text (.txt)" },
                        ]}
                      />
                    )}
                  />
                </div>
                {dataGranularity === "PER_ITEM" && (
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Filename field <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <Input<ApiEndpointType>
                      name="filename_field"
                      control={control}
                      placeholder="e.g. invoice_no"
                      disabled={isLoading}
                    />
                  </div>
                )}
                {/* Mixed response: also pull a file referenced inside each record */}
                <div className="space-y-1 md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">
                    File field <span className="text-gray-400 font-normal">(optional — also import the file)</span>
                  </label>
                  <Input<ApiEndpointType>
                    name="data_file_field"
                    control={control}
                    placeholder="e.g. attachment_url"
                    disabled={isLoading}
                  />
                  <p className="text-xs text-gray-500">
                    If each record also contains a file URL/base64, set its dot-path here.
                    DocxIQ imports the JSON <strong>and</strong> downloads that file.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ───── Pagination ───── */}
          <div className="col-span-1 md:col-span-2 border-t border-gray-200 pt-5 mt-1 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Pagination</h4>
              <p className="text-xs text-gray-500 mt-0.5">
                If the API returns results across multiple pages, DocxIQ walks every page and
                combines them before importing.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <Controller
                  name="pagination_type"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      value={field.value || "NONE"}
                      onChange={field.onChange}
                      disabled={isLoading}
                      options={[
                        { value: "NONE", label: "None", sublabel: "Single request" },
                        { value: "PAGE", label: "Page number", sublabel: "?page=2&limit=100" },
                        { value: "OFFSET", label: "Offset", sublabel: "?offset=100&limit=100" },
                        { value: "CURSOR", label: "Cursor / token", sublabel: "follow next_cursor" },
                      ]}
                    />
                  )}
                />
              </div>
              {paginationType && paginationType !== "NONE" && (
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Max pages <span className="text-gray-400 font-normal">(safety cap)</span>
                  </label>
                  <Input<ApiEndpointType>
                    name="max_pages"
                    control={control}
                    type="number"
                    placeholder="50"
                    disabled={isLoading}
                  />
                </div>
              )}
            </div>

            {(paginationType === "PAGE" || paginationType === "OFFSET") && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {paginationType === "PAGE" ? (
                  <>
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">Page param</label>
                      <Input<ApiEndpointType> name="page_param" control={control} placeholder="page" disabled={isLoading} />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">First page</label>
                      <Input<ApiEndpointType> name="start_page" control={control} type="number" placeholder="1" disabled={isLoading} />
                    </div>
                  </>
                ) : (
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Offset param</label>
                    <Input<ApiEndpointType> name="offset_param" control={control} placeholder="offset" disabled={isLoading} />
                  </div>
                )}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Size param</label>
                  <Input<ApiEndpointType> name="size_param" control={control} placeholder="limit" disabled={isLoading} />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Page size</label>
                  <Input<ApiEndpointType> name="page_size" control={control} type="number" placeholder="100" disabled={isLoading} />
                </div>
              </div>
            )}

            {paginationType === "CURSOR" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Cursor param</label>
                  <Input<ApiEndpointType> name="cursor_param" control={control} placeholder="cursor" disabled={isLoading} />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Next-cursor path</label>
                  <Input<ApiEndpointType> name="next_cursor_path" control={control} placeholder="e.g. meta.next_cursor" disabled={isLoading} />
                </div>
                <div className="md:col-span-3 space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Page size</label>
                  <div className="max-w-[160px]">
                    <Input<ApiEndpointType> name="page_size" control={control} type="number" placeholder="100" disabled={isLoading} />
                  </div>
                </div>
              </div>
            )}
          </div>

      </form>
    </div>


    {/* ✅ Sticky Footer */}
     <div className="flex-shrink-0 flex justify-end items-center gap-3 p-4 bg-white border-t border-gray-200">
    <button
      type="button"
      onClick={onClose}
      disabled={isLoading}
      className="flex items-center gap-2 px-5 text-sm py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
    >
      Cancel
    </button>

    <button
      type="submit"
      form="api-config-form"
      disabled={isLoading}
      onClick={handleSubmit(submitHandler)}
      className="px-5 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 transition"
    >
      {isLoading && (
        <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
      )}
      {config ? "Update Configuration" : "Create Configuration"}
    </button>
  </div>

    </div>

    </div>
  );
};

export default ApiEndpointForm;
